'use client'

import { useState, useEffect } from 'react'
import Head from 'next/head'
import EmailCaptureModal from '@/app/components/EmailCaptureModal'
import { incrementToolUsage } from '@/lib/trackUsage'

interface Idea {
  id: string;
  content: string;
  isStarred: boolean;
}

export default function RapidIdeatorTool() {
  const [prompt, setPrompt] = useState('')
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [backupIdeas, setBackupIdeas] = useState<Idea[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false)

  // Check if user has already submitted email
  useEffect(() => {
    const hasSubmitted = localStorage.getItem('hasSubmittedEmail')
    setHasSubmittedEmail(hasSubmitted === 'true')
    
    // Show modal after a short delay if they haven't submitted
    if (!hasSubmitted) {
      const timer = setTimeout(() => {
        setShowEmailModal(true)
      }, 2000) // Show after 2 seconds
      return () => clearTimeout(timer)
    }
  }, [])

  const handleEmailSuccess = () => {
    setHasSubmittedEmail(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!prompt.trim()) {
      setError('Please enter what you want to create')
      return
    }
    
    try {
      setLoading(true)
      setError('')
      
      // Track tool usage
      await incrementToolUsage('rapid-ideator')
      
      const response = await fetch('/api/rapid-ideator', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      })
      
      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate ideas')
      }
      
      // Generate unique IDs for each idea
      const newIdeas = result.ideas.map((idea: string, index: number) => ({
        id: `idea-${index}`,
        content: idea,
        isStarred: false
      }))
      
      // Split into main ideas and backup ideas
      setIdeas(newIdeas.slice(0, 10))
      setBackupIdeas(newIdeas.slice(10))
      
    } catch (err) {
      setError((err as Error).message || 'Error generating ideas. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const starIdea = (id: string) => {
    setIdeas(prevIdeas => {
      const ideaIndex = prevIdeas.findIndex(idea => idea.id === id)
      if (ideaIndex === -1) return prevIdeas
      
      const updatedIdeas = [...prevIdeas]
      const idea = updatedIdeas[ideaIndex]
      const newStarredState = !idea.isStarred
      
      // Remove the idea from its current position
      updatedIdeas.splice(ideaIndex, 1)
      
      // If it's being starred, add it to the top, otherwise add it after starred ideas
      if (newStarredState) {
        return [{ ...idea, isStarred: true }, ...updatedIdeas]
      } else {
        // Find the last starred idea
        const lastStarredIndex = updatedIdeas.findIndex(i => !i.isStarred)
        const insertIndex = lastStarredIndex === -1 ? updatedIdeas.length : lastStarredIndex
        updatedIdeas.splice(insertIndex, 0, { ...idea, isStarred: false })
        return updatedIdeas
      }
    })
  }

  const removeIdea = async (id: string) => {
    setIdeas(prevIdeas => {
      const filteredIdeas = prevIdeas.filter(idea => idea.id !== id)
      
      // Add a new idea from backup if available
      if (backupIdeas.length > 0) {
        const newBackupIdeas = [...backupIdeas]
        const nextIdea = newBackupIdeas.shift()!
        setBackupIdeas(newBackupIdeas)
        return [...filteredIdeas, nextIdea]
      }
      
      return filteredIdeas
    })

    // If backups are running low (less than 3), generate more ideas
    if (backupIdeas.length < 3) {
      try {
        const response = await fetch('/api/rapid-ideator', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ prompt }),
        })
        
        const result = await response.json()
        
        if (response.ok) {
          // Generate unique IDs for new backup ideas
          const newBackupIdeas = result.ideas.map((idea: string, index: number) => ({
            id: `backup-${Date.now()}-${index}`,
            content: idea,
            isStarred: false
          }))
          
          // Add all new ideas to backup pool
          setBackupIdeas(prev => [...prev, ...newBackupIdeas])
        }
      } catch (err) {
        // Silently fail - we don't want to interrupt the user experience
        console.error('Failed to generate new backup ideas:', err)
      }
    }
  }

  return (
    <section>
      <Head>
        <title>Rapid Ideator | GrandLine Labs</title>
        <meta name="description" content="Generate creative ideas quickly with our AI-powered Rapid Ideator tool. Get instant inspiration for your next project or creation." />
        <meta name="keywords" content="idea generation, creativity tool, brainstorming, AI ideas, creative inspiration" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://grandlinelabs.com/tools/rapid_ideator" />
        <meta property="og:title" content="Rapid Ideator | GrandLine Labs" />
        <meta property="og:description" content="Generate creative ideas quickly with our AI-powered Rapid Ideator tool. Get instant inspiration for your next project or creation." />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://grandlinelabs.com/tools/rapid_ideator" />
        <meta property="twitter:title" content="Rapid Ideator | GrandLine Labs" />
        <meta property="twitter:description" content="Generate creative ideas quickly with our AI-powered Rapid Ideator tool. Get instant inspiration for your next project or creation." />
        
        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "Rapid Ideator",
              "description": "Generate creative ideas quickly with our AI-powered Rapid Ideator tool. Get instant inspiration for your next project or creation.",
              "url": "https://grandlinelabs.com/tools/rapid_ideator",
              "applicationCategory": "CreativityApplication",
              "operatingSystem": "Web Browser",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "featureList": [
                "AI-powered idea generation",
                "Instant creative inspiration",
                "Idea locking and management",
                "Backup ideas system"
              ]
            })
          }}
        />
      </Head>

      <h1 className="font-semibold text-2xl mb-8 tracking-tighter">Rapid Ideator</h1>
      
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="flex flex-col gap-4">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="What are you creating? Be specific about your goals..."
            className="w-full p-4 border rounded-lg dark:bg-neutral-800 dark:border-neutral-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] resize-y"
            rows={4}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-400 text-white rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                  </svg>
                  <span>Generate Ideas</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      <div className="space-y-4">
        {ideas.map((idea) => (
          <div
            key={idea.id}
            className={`p-4 rounded-lg border ${
              idea.isStarred
                ? 'border-yellow-400 dark:border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                : 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-800'
            } flex justify-between items-center group transition-all`}
          >
            <p className="text-lg">{idea.content}</p>
            <div className="flex gap-2">
              <button
                onClick={() => starIdea(idea.id)}
                className={`p-2 rounded-full transition-colors cursor-pointer ${
                  idea.isStarred
                    ? 'text-yellow-400 hover:text-yellow-500 dark:text-yellow-500 dark:hover:text-yellow-400'
                    : 'text-neutral-400 hover:text-yellow-400 dark:text-neutral-500 dark:hover:text-yellow-400'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill={idea.isStarred ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
              <button
                onClick={() => removeIdea(idea.id)}
                className="p-2 rounded-full text-neutral-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400 transition-colors cursor-pointer"
                title="Remove idea"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="prose dark:prose-invert max-w-none mt-8">
        <p className="text-neutral-600 dark:text-neutral-400">
          Get instant creative inspiration with our AI-powered Rapid Ideator tool. Simply tell us what you want to create, and we'll generate a variety of creative ideas to help you get started. Lock your favorite ideas and remove ones you don't like to get fresh suggestions.
        </p>
        <h2 className="text-xl font-semibold mt-6 mb-4">Features</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>AI-powered idea generation</li>
          <li>Instant creative inspiration</li>
          <li>Lock your favorite ideas</li>
          <li>Remove and replace ideas</li>
          <li>Backup ideas system</li>
          <li>Clean, intuitive interface</li>
        </ul>
      </div>

      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
      />
    </section>
  )
} 